import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { getAuthors, getAuthorPublishedArticles, toggleReviewer, updateAuthor, deleteAuthor } from '../api/client';
import { useAuth } from '../App';

const COUNTRY_LABELS = {
  UZ: "🇺🇿 O'zbekiston",
  RU: '🇷🇺 Rossiya',
  KZ: "🇰🇿 Qozog'iston",
  KG: "🇰🇬 Qirg'iziston",
  TJ: '🇹🇯 Tojikiston',
  TM: '🇹🇲 Turkmaniston',
  AZ: '🇦🇿 Ozarbayjon',
  GE: '🇬🇪 Gruziya',
  US: '🇺🇸 AQSH',
  GB: '🇬🇧 Britaniya',
  DE: '🇩🇪 Germaniya',
  FR: '🇫🇷 Fransiya',
  TR: '🇹🇷 Turkiya',
  CN: '🇨🇳 Xitoy',
  JP: '🇯🇵 Yaponiya',
  KR: '🇰🇷 Koreya',
  IN: '🇮🇳 Hindiston',
};

const LANG_LABELS = { uz: "🇺🇿 O'zbekcha", ru: '🇷🇺 Ruscha', en: '🇬🇧 Inglizcha' };

function AuthorModal({ author, publishedArticles, articlesLoading, onClose, onToggleReviewer, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  if (!author) return null;

  const startEdit = () => {
    setForm({
      first_name: author.first_name || '',
      last_name: author.last_name || '',
      middle_name: author.middle_name || '',
      email: author.email || '',
      phone: author.phone || '',
      orcid: author.orcid || '',
      affiliation: author.affiliation || '',
      country: author.country || '',
      first_name_en: author.first_name_en || '',
      last_name_en: author.last_name_en || '',
      middle_name_en: author.middle_name_en || '',
      affiliation_en: author.affiliation_en || '',
      first_name_ru: author.first_name_ru || '',
      last_name_ru: author.last_name_ru || '',
      middle_name_ru: author.middle_name_ru || '',
      affiliation_ru: author.affiliation_ru || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(author.id, form);
      setEditing(false);
    } catch (err) {
      // handled in parent
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await onDelete(author.id);
    } catch (err) {
      // handled in parent
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
  };

  const field = (label, key, type = 'text') => (
    <div>
      <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1 block">{label}</label>
      <input
        type={type}
        value={form[key] || ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {author.last_name} {author.first_name} {author.middle_name || ''}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">ID: {author.id} &middot; Telegram: {author.telegram_id}</p>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <>
                <button onClick={startEdit} className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-blue-600" title="Tahrirlash">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button onClick={() => setConfirmDelete(true)} className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-500" title="O'chirish">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Delete Confirmation */}
        {confirmDelete && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm font-medium text-red-800 mb-1">Muallifni o'chirmoqchimisiz?</p>
            <p className="text-xs text-red-600 mb-3">Muallif va uning barcha maqolalari o'chiriladi. Bu amalni qaytarib bo'lmaydi!</p>
            <div className="flex gap-2">
              <button onClick={handleDelete} disabled={saving} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                {saving ? "O'chirilmoqda..." : "Ha, o'chirish"}
              </button>
              <button onClick={() => setConfirmDelete(false)} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">
                Bekor qilish
              </button>
            </div>
          </div>
        )}

        <div className="p-6 space-y-5">
          {editing ? (
            /* ── Edit Form ── */
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {field('Familiya', 'last_name')}
                {field('Ism', 'first_name')}
                {field('Sharif', 'middle_name')}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field('Email', 'email', 'email')}
                {field('Telefon', 'phone', 'tel')}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field('ORCID', 'orcid')}
                {field('Ish joyi', 'affiliation')}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1 block">Davlat</label>
                <select
                  value={form.country || ''}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">Tanlanmagan</option>
                  {Object.entries(COUNTRY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <p className="text-xs font-semibold text-slate-500 mt-2">🇬🇧 Inglizcha</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {field('Last name', 'last_name_en')}
                {field('First name', 'first_name_en')}
                {field('Middle name', 'middle_name_en')}
              </div>
              {field('Affiliation (EN)', 'affiliation_en')}

              <p className="text-xs font-semibold text-slate-500 mt-2">🇷🇺 Русча</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {field('Фамилия', 'last_name_ru')}
                {field('Имя', 'first_name_ru')}
                {field('Отчество', 'middle_name_ru')}
              </div>
              {field('Место работы (RU)', 'affiliation_ru')}

              <div className="flex gap-2 pt-2">
                <button onClick={handleSave} disabled={saving} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saqlanmoqda...' : 'Saqlash'}
                </button>
                <button onClick={() => setEditing(false)} className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200">
                  Bekor qilish
                </button>
              </div>
            </div>
          ) : (
            /* ── View Mode ── */
            <>
              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Email</p>
                  <p className="text-sm text-slate-900">{author.email || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Telefon</p>
                  <p className="text-sm text-slate-900">{author.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">ORCID</p>
                  {author.orcid ? (
                    <a href={`https://orcid.org/${author.orcid}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700">
                      {author.orcid}
                    </a>
                  ) : <p className="text-sm text-slate-400">-</p>}
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Ish joyi</p>
                  <p className="text-sm text-slate-900">{author.affiliation || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Davlat</p>
                  <p className="text-sm text-slate-900">{COUNTRY_LABELS[author.country] || author.country || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Til</p>
                  <p className="text-sm text-slate-900">{LANG_LABELS[author.language] || author.language || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Ro'yxatdan o'tgan sana</p>
                  <p className="text-sm text-slate-900">{formatDate(author.created_at)}</p>
                </div>
              </div>

	              {/* Multilingual Names */}
	              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Turli tillardagi F.I.O va ish joyi</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-slate-500 mb-1">🇺🇿 O'zbekcha</p>
                    <p className="text-sm font-medium text-slate-900">{author.last_name} {author.first_name} {author.middle_name || ''}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{author.affiliation || '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-slate-500 mb-1">🇬🇧 Inglizcha</p>
                    <p className="text-sm font-medium text-slate-900">{author.last_name_en || '-'} {author.first_name_en || ''} {author.middle_name_en || ''}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{author.affiliation_en || '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-slate-500 mb-1">🇷🇺 Ruscha</p>
                    <p className="text-sm font-medium text-slate-900">{author.last_name_ru || '-'} {author.first_name_ru || ''} {author.middle_name_ru || ''}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{author.affiliation_ru || '-'}</p>
                  </div>
	                </div>
	              </div>

              {/* Published Articles */}
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Ushbu jurnalda chop etilgan maqolalari</p>
                {articlesLoading ? (
                  <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-500">Yuklanmoqda...</div>
                ) : publishedArticles?.length ? (
                  <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                    {publishedArticles.map((item) => (
                      <a
                        key={item.id}
                        href={`/#/submissions/${item.id}?issue=${item.issue_id}`}
                        className="block bg-slate-50 hover:bg-slate-100 rounded-xl p-3 transition-colors"
                      >
                        <p className="text-sm font-medium text-slate-900">{item.title}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {item.issue_title} {item.issue_year ? `(${item.issue_year})` : ''}
                        </p>
                        <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                          <span>{formatDate(item.published_at)}</span>
                          {item.doi && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">
                              DOI: {item.doi}
                            </span>
                          )}
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-500">Hozircha chop etilgan maqola yo'q</div>
                )}
              </div>

	              {/* Reviewer Toggle */}
	              <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
                <div>
                  <p className="text-sm font-medium text-slate-900">Taqrizchi sifatida belgilash</p>
                  <p className="text-xs text-slate-400 mt-0.5">Ushbu muallif maqolalarni taqrizlash uchun tayinlanishi mumkin</p>
                </div>
                <button
                  onClick={() => onToggleReviewer(author.id)}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                    author.is_reviewer ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                    author.is_reviewer ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AuthorsPage() {
  const { selectedJournalSlug, currentJournal } = useAuth();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [authorPublishedArticles, setAuthorPublishedArticles] = useState([]);
  const [authorArticlesLoading, setAuthorArticlesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    setSelectedAuthor(null);
    setAuthorPublishedArticles([]);
    loadAuthors();
  }, [selectedJournalSlug]);

  useEffect(() => {
    if (!selectedAuthor?.id) {
      setAuthorPublishedArticles([]);
      setAuthorArticlesLoading(false);
      return;
    }
    const loadAuthorArticles = async () => {
      setAuthorArticlesLoading(true);
      try {
        const res = await getAuthorPublishedArticles(selectedAuthor.id);
        setAuthorPublishedArticles(res.data || []);
      } catch (err) {
        setAuthorPublishedArticles([]);
        toast.error("Muallifning chop etilgan maqolalarini yuklab bo'lmadi");
      } finally {
        setAuthorArticlesLoading(false);
      }
    };
    loadAuthorArticles();
  }, [selectedAuthor?.id, selectedJournalSlug]);

  const loadAuthors = async () => {
    setLoading(true);
    try {
      const res = await getAuthors();
      setAuthors(res.data);
    } catch (err) {
      console.error('Failed to load authors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReviewer = async (authorId) => {
    try {
      const res = await toggleReviewer(authorId);
      const updated = res.data;
      setAuthors((prev) =>
        prev.map((a) => (a.id === authorId ? updated : a))
      );
      if (selectedAuthor && selectedAuthor.id === authorId) {
        setSelectedAuthor(updated);
      }
      toast.success(updated.is_reviewer ? "Taqrizchi sifatida belgilandi" : "Taqrizchi belgisi olib tashlandi");
    } catch (err) {
      toast.error('Xatolik');
    }
  };

  const handleUpdateAuthor = async (authorId, data) => {
    try {
      const res = await updateAuthor(authorId, data);
      const updated = res.data;
      setAuthors((prev) =>
        prev.map((a) => (a.id === authorId ? updated : a))
      );
      setSelectedAuthor(updated);
      toast.success("Muallif ma'lumotlari yangilandi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yangilashda xatolik");
      throw err;
    }
  };

  const handleDeleteAuthor = async (authorId) => {
    try {
      await deleteAuthor(authorId);
      setAuthors((prev) => prev.filter((a) => a.id !== authorId));
      setSelectedAuthor(null);
      toast.success("Muallif o'chirildi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "O'chirishda xatolik");
      throw err;
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return authors;
    const q = search.toLowerCase();
    return authors.filter(
      (a) =>
        a.first_name?.toLowerCase().includes(q) ||
        a.last_name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.orcid?.includes(q) ||
        a.affiliation?.toLowerCase().includes(q)
    );
  }, [authors, search]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const sortedAuthors = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'name': valA = a.last_name || ''; valB = b.last_name || ''; break;
        case 'email': valA = a.email || ''; valB = b.email || ''; break;
        case 'affiliation': valA = a.affiliation || ''; valB = b.affiliation || ''; break;
        default: return 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortField, sortDirection]);

  const totalPages = Math.ceil(sortedAuthors.length / itemsPerPage);
  const paginatedAuthors = sortedAuthors.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [search]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-slate-300 ml-1">↕</span>;
    return <span className="text-blue-600 ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Mualliflar</h1>
          <p className="mt-1 text-sm text-slate-500">
            {currentJournal?.short_name || currentJournal?.name || 'Tanlangan jurnal'}
          </p>
        </div>
        <span className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-medium">
          Jami: {authors.length}
        </span>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Qidirish (ism, email, ORCID, ish joyi)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-96 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600" onClick={() => handleSort('name')}>F.I.O<SortIcon field="name" /></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600" onClick={() => handleSort('email')}>Email<SortIcon field="email" /></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Telefon</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">ORCID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600" onClick={() => handleSort('affiliation')}>Ish joyi<SortIcon field="affiliation" /></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {paginatedAuthors.map((author, idx) => (
              <tr
                key={author.id}
                className="hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => setSelectedAuthor(author)}
              >
                <td className="px-4 py-3 text-sm text-slate-400">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">
                  <div className="flex items-center gap-2">
                    {author.last_name} {author.first_name} {author.middle_name || ''}
                    {author.is_reviewer && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700">Taqrizchi</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{author.email}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{author.phone || '-'}</td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {author.orcid ? (
                    <a
                      href={`https://orcid.org/${author.orcid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {author.orcid}
                    </a>
                  ) : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 max-w-[200px] truncate">
                  {author.affiliation || '-'}
                </td>
              </tr>
            ))}
            {paginatedAuthors.length === 0 && (
              <tr>
                <td colSpan="6" className="px-4 py-12 text-center text-slate-400 text-sm">
                  Mualliflar topilmadi
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {sortedAuthors.length > 0 && (
        <div className="flex items-center justify-between mt-4 bg-white rounded-2xl border border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Sahifada:</span>
            {[20, 50, 100].map(n => (
              <button key={n} onClick={() => { setItemsPerPage(n); setCurrentPage(1); }}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${itemsPerPage === n ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{n}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">←</button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let page;
              if (totalPages <= 7) { page = i + 1; }
              else if (currentPage <= 4) { page = i + 1; }
              else if (currentPage >= totalPages - 3) { page = totalPages - 6 + i; }
              else { page = currentPage - 3 + i; }
              return (
                <button key={page} onClick={() => setCurrentPage(page)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${currentPage === page ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}>{page}</button>
              );
            })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">→</button>
          </div>
          <span className="text-xs text-slate-400">{sortedAuthors.length} tadan {(currentPage - 1) * itemsPerPage + 1}—{Math.min(currentPage * itemsPerPage, sortedAuthors.length)}</span>
        </div>
      )}

      {/* Author Detail Modal */}
      {selectedAuthor && (
        <AuthorModal
          author={selectedAuthor}
          publishedArticles={authorPublishedArticles}
          articlesLoading={authorArticlesLoading}
          onClose={() => setSelectedAuthor(null)}
          onToggleReviewer={handleToggleReviewer}
          onUpdate={handleUpdateAuthor}
          onDelete={handleDeleteAuthor}
        />
      )}
    </div>
  );
}
